import { RefObject, useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Sends a scroll container back to the top whenever the route changes.
 *
 * The app body never scrolls — `.layout` is `overflow: hidden` and the content
 * area (`.rs-content`) is the real scroller. React Router only swaps the
 * element inside it, so that container keeps whatever `scrollTop` the previous
 * page left behind: click a quick action from halfway down the dashboard and
 * the next page opens scrolled to the same offset, showing its middle instead
 * of its heading.
 *
 * Keyed on `pathname` only. Query-string changes (tab switches, pagination,
 * filters) stay put on purpose — yanking the view to the top mid-interaction
 * is worse than leaving it alone.
 *
 * `useLayoutEffect` so the reset lands in the same frame the new page paints;
 * with a plain effect the old offset is briefly visible.
 */
export const useScrollToTopOnNavigate = (
  ref: RefObject<HTMLElement | null>
): void => {
  const { pathname } = useLocation()

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    // Direct assignment rather than `scrollTo` — no smooth-scroll animation to
    // fight the page transition, and it works in jsdom.
    el.scrollTop = 0
    el.scrollLeft = 0
  }, [pathname, ref])
}

export default useScrollToTopOnNavigate
